import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as actions from "../actions/actions";
import {
  getEmail,
  getIsAuthenticated,
  getIsLoggedOut,
  getOrgOptionAllowlistLoaded,
} from "../selectors/selectors";
import {
  fetchShowOrgOptionAllowlist,
  isUserInShowOrgOptionAllowlist,
} from "../services/orgOptionConfigurationService";

type UseOrgOptionAllowlistOptions = {
  /** True after dashboard session verification succeeds. */
  sessionReady?: boolean;
};

/**
 * Fetches the org-creation allowlist once per authenticated session after login.
 * @param {UseOrgOptionAllowlistOptions} [root0] Hook options
 * @param {boolean} [root0.sessionReady] True after dashboard session verification succeeds
 * @returns {void}
 */
const useOrgOptionAllowlist = ({
  sessionReady = false,
}: UseOrgOptionAllowlistOptions = {}) => {
  const dispatch = useDispatch();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const isLoggedOut = useSelector(getIsLoggedOut);
  const userEmail = useSelector(getEmail);
  const orgOptionAllowlistLoaded = useSelector(getOrgOptionAllowlistLoaded);
  const isFetchingRef = useRef(false);
  const hasAuthContext = isAuthenticated || !isLoggedOut || sessionReady;

  useEffect(() => {
    if (!hasAuthContext) {
      isFetchingRef.current = false;
      return;
    }

    const trimmedEmail = userEmail?.trim();
    if (!trimmedEmail || orgOptionAllowlistLoaded || isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;

    const loadOrgOptionAllowlist = async () => {
      try {
        const allowedEmails = await fetchShowOrgOptionAllowlist();
        const canCreateOrganization = isUserInShowOrgOptionAllowlist(
          trimmedEmail,
          allowedEmails,
        );
        dispatch(actions.setCanCreateOrganization(canCreateOrganization));
      } catch (error) {
        console.error("Error fetching org option configuration:", error);
        dispatch(actions.setCanCreateOrganization(false));
      } finally {
        isFetchingRef.current = false;
      }
    };

    loadOrgOptionAllowlist();
  }, [
    dispatch,
    hasAuthContext,
    orgOptionAllowlistLoaded,
    sessionReady,
    userEmail,
  ]);
};

export default useOrgOptionAllowlist;
